import React from 'react';
import cl from 'classnames';
import scss from './Button.module.scss';
import './Button.scss';

// Button sizing constants
const SIZING = {
    xs: scss.sizeXS,
    sm: scss.sizeSM,
    md: scss.sizeMD,
    lg: scss.sizeLG,
};

// Button color constants
const COLORS = {
    primary: scss.colorPrimary,
    secondary: scss.Secondary,
    standard: scss.colorStandard,
    dark: scss.colorDark,
};

// Type definitions
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type ButtonLook = 'primary' | 'secondary' | 'standard' | 'dark';
export type ButtonBaseLook = 'secondary' | 'outline' | 'dark' | 'white' | 'ghost' | 'link';

/**
 * Props for the legacy Button component
 */
export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    /** Button size variant */
    size?: ButtonSize;
    /** Button color/look variant */
    look?: ButtonLook;
    /** Additional CSS class names */
    className?: string;
};

/**
 * Props for the main Button component
 */
export type BaseButtonProps = {
    /** Button size variant */
    size?: ButtonSize;
    /** Button look variant */
    look?: ButtonBaseLook;
    /** Whether button should stretch to full width */
    stretch?: boolean;
    /** Whether button is in loading state */
    isLoading?: boolean;
    /** Additional CSS class names */
    className?: string;
    /** Custom element type to render (defaults to 'button') */
    as?: React.ElementType;
};

/**
 * Generates CSS classes for the legacy button styling
 * @param look - Button look variant
 * @param size - Button size variant
 * @param additionalClassName - Additional CSS classes to append
 * @returns Space-separated string of CSS class names
 */
export function getButtonClasses(
    look: ButtonLook, 
    size: ButtonSize, 
    additionalClassName = ''
): string {
    return [
        scss.base,
        COLORS[look],
        SIZING[size],
        additionalClassName,
    ].join(' ');
}

/**
 * Generates CSS classes for the base button styling
 * @param look - Button look variant
 * @param size - Button size variant
 * @returns Space-separated string of CSS class names
 */
function getBaseButtonClasses(look?: ButtonBaseLook, size?: ButtonSize): string {
    return [
        'ew-btn',
        look && `ew-btn--${look}`,
        size && `ew-btn--${size}`,
    ].join(' ');
}

/**
 * Main Button component with flexible styling and behavior options
 * 
 * @template T - Additional props type extending button HTML attributes
 * @param props - Component props
 * @param props.look - Button look variant (secondary, outline, dark, white, ghost, link)
 * @param props.size - Button size variant (xs, sm, md, lg)
 * @param props.stretch - Whether button should stretch to full width
 * @param props.isLoading - Whether button is in loading state
 * @param props.className - Additional CSS class names
 * @param props.as - Custom element type to render (defaults to 'button')
 * @param props.children - Button content
 * @returns React element
 * 
 * @example
 * ```tsx
 * // Basic button
 * <Button>Click me</Button>
 * 
 * // Styled button
 * <Button look="secondary" stretch>
 *   Reset
 * </Button>
 * 
 * // Loading button
 * <Button isLoading disabled>
 *   Processing...
 * </Button>
 * 
 * // Custom element
 * <Button as="a" href="/link">
 *   Link Button
 * </Button>
 * ```
 */
export default function Button<T = React.ButtonHTMLAttributes<HTMLButtonElement>>({
    look,
    size,
    className,
    as,
    stretch = false,
    isLoading = false,
    ...props
}: BaseButtonProps & T) {
    const Comp = as || 'button';
    
    return (
        <Comp
            className={cl(
                getBaseButtonClasses(look, size),
                className,
                {
                    'ew-btn--stretch': stretch,
                    'ew-btn--loading': isLoading,
                }
            )}
            {...props}
        />
    );
}